import { useState } from 'react';
import { Box } from '@mui/material';

import { Order } from '@barboxx/models';

const FACE_PREVIEW_SIZE = 240;

const HARD_FAIL_REASON_CODES: ReadonlySet<Order.PickupFaceVerificationReasonCode> =
    new Set([
        'NO_FACE_DETECTED',
        'MULTIPLE_SOURCE_FACES',
        'PARTIAL_FACE',
        'VERY_LOW_SHARPNESS',
    ]);

const SOFT_REASON_CODES: ReadonlySet<Order.PickupFaceVerificationReasonCode> =
    new Set([
        'FACE_OCCLUDED',
        'LOW_SHARPNESS',
        'LOW_BRIGHTNESS',
        'LOW_SIMILARITY',
    ]);

function getFaceBoxColor(
    reasonCodes?: Order.PickupFaceVerificationReasonCode[],
    decisionOutcome?: Order.PickupFaceVerificationDecisionOutcome
): string {
    if (reasonCodes && reasonCodes.length > 0) {
        if (reasonCodes.some(rc => HARD_FAIL_REASON_CODES.has(rc))) {
            return 'error.main';
        }

        if (reasonCodes.some(rc => SOFT_REASON_CODES.has(rc))) {
            return 'warning.main';
        }

        return 'success.main';
    }

    if (decisionOutcome === 'review') {
        return 'error.main';
    }

    if (decisionOutcome === 'retry') {
        return 'warning.main';
    }

    if (decisionOutcome === 'approve') {
        return 'success.main';
    }

    return 'text.disabled';
}

interface RenderFrame {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
}

const toFramePixel = (value: number | null | undefined, size: number) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return 0;
    }

    return value * size;
};

const getCoverRenderFrame = (
    containerSize: number,
    imageWidth: number,
    imageHeight: number
): RenderFrame => {
    if (imageWidth <= 0 || imageHeight <= 0) {
        return {
            offsetX: 0,
            offsetY: 0,
            width: containerSize,
            height: containerSize,
        };
    }

    const scale = Math.max(
        containerSize / imageWidth,
        containerSize / imageHeight
    );

    const width = imageWidth * scale;
    const height = imageHeight * scale;

    return {
        offsetX: (containerSize - width) / 2,
        offsetY: (containerSize - height) / 2,
        width,
        height,
    };
};

export function FaceDiagnosticsPreview({
    imageUrl,
    alt,
    faces,
    reasonCodes,
    decisionOutcome,
    referenceOnly = false,
    showDebugOverlays = true,
}: {
    imageUrl: string;
    alt: string;
    faces: Order.PickupFaceVerificationFace[];
    reasonCodes?: Order.PickupFaceVerificationReasonCode[];
    decisionOutcome?: Order.PickupFaceVerificationDecisionOutcome;
    referenceOnly?: boolean;
    showDebugOverlays?: boolean;
}) {
    const [imageSize, setImageSize] = useState({
        width: FACE_PREVIEW_SIZE,
        height: FACE_PREVIEW_SIZE,
    });

    const frame = getCoverRenderFrame(
        FACE_PREVIEW_SIZE,
        imageSize.width,
        imageSize.height
    );

    const boxColor = referenceOnly
        ? 'info.main'
        : getFaceBoxColor(reasonCodes, decisionOutcome);

    return (
        <Box
            sx={{
                position: 'relative',
                width: FACE_PREVIEW_SIZE,
                height: FACE_PREVIEW_SIZE,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'black',
            }}
        >
            <Box
                component="img"
                src={imageUrl}
                alt={alt}
                onLoad={event => {
                    const { naturalWidth, naturalHeight } = event.currentTarget;
                    if (naturalWidth > 0 && naturalHeight > 0) {
                        setImageSize({
                            width: naturalWidth,
                            height: naturalHeight,
                        });
                    }
                }}
                sx={{
                    width: FACE_PREVIEW_SIZE,
                    height: FACE_PREVIEW_SIZE,
                    objectFit: 'cover',
                    imageOrientation: 'from-image',
                }}
            />
            {showDebugOverlays ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                    }}
                >
                    {faces.map((face, faceIndex) => {
                        const box = face.boundingBox;

                        if (!box) {
                            return null;
                        }

                        const left =
                            frame.offsetX + toFramePixel(box.left, frame.width);
                        const top =
                            frame.offsetY + toFramePixel(box.top, frame.height);
                        const width = toFramePixel(box.width, frame.width);
                        const height = toFramePixel(box.height, frame.height);

                        return (
                            <Box key={`face-${faceIndex}`}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left,
                                        top,
                                        width,
                                        height,
                                        border: '2px solid',
                                        borderColor: boxColor,
                                        borderRadius: 0.5,
                                    }}
                                />
                                {face.landmarks.map(
                                    (landmark, landmarkIndex) => {
                                        const x =
                                            frame.offsetX +
                                            toFramePixel(
                                                landmark.x,
                                                frame.width
                                            );
                                        const y =
                                            frame.offsetY +
                                            toFramePixel(
                                                landmark.y,
                                                frame.height
                                            );

                                        return (
                                            <Box
                                                key={`face-${faceIndex}-lm-${landmarkIndex}`}
                                                sx={{
                                                    position: 'absolute',
                                                    left: x - 2,
                                                    top: y - 2,
                                                    width: 4,
                                                    height: 4,
                                                    borderRadius: '50%',
                                                    backgroundColor:
                                                        'success.main',
                                                }}
                                            />
                                        );
                                    }
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ) : null}
        </Box>
    );
}
